import { useEffect, useState } from "react";
import { api, formatDate, initials } from "../lib/api";

const LIKE_KEY = "espacio_entre_likes";

const getLikedSet = () => {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(LIKE_KEY) || "[]"));
  } catch {
    return new Set();
  }
};
const saveLikedSet = (set) =>
  sessionStorage.setItem(LIKE_KEY, JSON.stringify([...set]));

export default function Comentarios({ slug }) {
  const [threads, setThreads] = useState([]);
  const [form, setForm] = useState({ nombre: "", email: "", texto: "" });
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [liked, setLiked] = useState(getLikedSet());
  // {parent_id → {nombre, texto, submitting}}
  const [replyState, setReplyState] = useState({});
  // which comment has the reply form open
  const [replyOpenId, setReplyOpenId] = useState(null);

  const load = async () => {
    try {
      const res = await api.get(`/comentarios/${slug}`);
      setThreads(res.data);
    } catch {
      setThreads([]);
    }
  };

  useEffect(() => {
    load();
  }, [slug]);

  const showToast = (msg, type = "ok") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.texto.trim()) {
      showToast("Nombre y texto son obligatorios", "error");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        post_slug: slug,
        nombre: form.nombre,
        texto: form.texto,
      };
      if (form.email.trim()) payload.email = form.email.trim();
      const res = await api.post(`/comentarios`, payload);
      // Add to the top of the list immediately (comment is already approved)
      setThreads((prev) => [{ ...res.data, respuestas: [] }, ...prev]);
      setForm({ nombre: "", email: "", texto: "" });
      showToast("Comentario publicado");
    } catch (err) {
      showToast("No pudimos publicar tu comentario", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const submitReply = async (parentId, e) => {
    e.preventDefault();
    const st = replyState[parentId] || {};
    if (!st.nombre?.trim() || !st.texto?.trim()) {
      showToast("Nombre y texto son obligatorios", "error");
      return;
    }
    setReplyState((s) => ({ ...s, [parentId]: { ...st, submitting: true } }));
    try {
      const res = await api.post(`/comentarios`, {
        post_slug: slug,
        nombre: st.nombre,
        texto: st.texto,
        parent_id: parentId,
      });
      // Attach the new reply under its parent (server flattens deeper levels)
      const attachId = res.data.parent_id || parentId;
      setThreads((prev) =>
        prev.map((c) =>
          c.id === attachId
            ? { ...c, respuestas: [...(c.respuestas || []), res.data] }
            : c
        )
      );
      setReplyState((s) => ({ ...s, [parentId]: { nombre: "", texto: "", submitting: false } }));
      setReplyOpenId(null);
      showToast("Respuesta publicada");
    } catch (err) {
      setReplyState((s) => ({ ...s, [parentId]: { ...st, submitting: false } }));
      showToast("No pudimos publicar tu respuesta", "error");
    }
  };

  const toggleLike = async (c) => {
    const has = liked.has(c.id);
    const action = has ? "unlike" : "like";
    // optimistic update — walk the tree to update the target comment
    const update = (arr) =>
      arr.map((x) => {
        if (x.id === c.id) return { ...x, likes: (x.likes || 0) + (has ? -1 : 1) };
        if (x.respuestas?.length) return { ...x, respuestas: update(x.respuestas) };
        return x;
      });
    setThreads((prev) => update(prev));
    const next = new Set(liked);
    if (has) next.delete(c.id);
    else next.add(c.id);
    setLiked(next);
    saveLikedSet(next);

    try {
      await api.post(`/comentarios/${c.id}/like?action=${action}`);
    } catch (err) {
      // rollback
      const rollback = (arr) =>
        arr.map((x) => {
          if (x.id === c.id) return { ...x, likes: (x.likes || 0) + (has ? 1 : -1) };
          if (x.respuestas?.length) return { ...x, respuestas: rollback(x.respuestas) };
          return x;
        });
      setThreads((prev) => rollback(prev));
      const revert = new Set(liked);
      if (has) revert.add(c.id);
      else revert.delete(c.id);
      setLiked(revert);
      saveLikedSet(revert);
      showToast("No pudimos registrar tu 'me identifico'", "error");
    }
  };

  const renderComment = (c, isReply = false) => (
    <div
      className={`comment-item ${isReply ? "comment-reply" : ""}`}
      key={c.id}
      data-testid={`comment-${c.id}`}
    >
      <div className="comment-avatar">{initials(c.nombre)}</div>
      <div>
        <div className="comment-head">
          <span className="comment-name">{c.nombre}</span>
          <span className="comment-date">{formatDate(c.fecha)}</span>
        </div>
        <p className="comment-text">{c.texto}</p>
        <div className="comment-actions">
          <button
            className={`like ${liked.has(c.id) ? "active" : ""}`}
            onClick={() => toggleLike(c)}
            data-testid={`like-btn-${c.id}`}
          >
            <span>♡</span> Me identifico ({c.likes || 0})
          </button>
          {!isReply && (
            <button
              onClick={() =>
                setReplyOpenId((open) => (open === c.id ? null : c.id))
              }
              data-testid={`reply-btn-${c.id}`}
            >
              {replyOpenId === c.id ? "Cancelar" : "Responder"}
            </button>
          )}
        </div>

        {replyOpenId === c.id && (
          <form
            className="reply-form"
            onSubmit={(e) => submitReply(c.id, e)}
            data-testid={`reply-form-${c.id}`}
          >
            <textarea
              placeholder="Tu respuesta…"
              value={replyState[c.id]?.texto || ""}
              onChange={(e) =>
                setReplyState((s) => ({
                  ...s,
                  [c.id]: { ...(s[c.id] || {}), texto: e.target.value },
                }))
              }
              data-testid={`reply-texto-${c.id}`}
            />
            <input
              type="text"
              placeholder="Tu nombre"
              value={replyState[c.id]?.nombre || ""}
              onChange={(e) =>
                setReplyState((s) => ({
                  ...s,
                  [c.id]: { ...(s[c.id] || {}), nombre: e.target.value },
                }))
              }
              data-testid={`reply-nombre-${c.id}`}
            />
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={replyState[c.id]?.submitting}
              data-testid={`reply-submit-${c.id}`}
            >
              {replyState[c.id]?.submitting ? "Enviando…" : "Enviar respuesta"}
            </button>
          </form>
        )}

        {c.respuestas?.length > 0 && (
          <div className="comment-replies" data-testid={`replies-of-${c.id}`}>
            {c.respuestas.map((r) => renderComment(r, true))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="comments-section" data-testid="comments-section">
      <form
        className="comment-form"
        onSubmit={submit}
        data-testid="comment-form"
      >
        <textarea
          placeholder="Lo que quieras compartir…"
          value={form.texto}
          onChange={(e) => setForm({ ...form, texto: e.target.value })}
          data-testid="comment-texto-input"
        />
        <div className="comment-form-row">
          <input
            type="text"
            placeholder="Tu nombre"
            value={form.nombre}
            onChange={(e) => setForm({ ...form, nombre: e.target.value })}
            data-testid="comment-nombre-input"
          />
          <input
            type="email"
            placeholder="Email (privado, opcional)"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="comment-email-input"
          />
        </div>
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={submitting}
          data-testid="comment-submit-btn"
        >
          {submitting ? "Publicando…" : "Publicar comentario"}
        </button>
      </form>

      <div className="comment-list" data-testid="comment-list">
        {threads.length === 0 ? (
          <p style={{ color: "var(--ink-3)", textAlign: "center" }}>
            Todavía no hay comentarios. Se el primero en escribir.
          </p>
        ) : (
          threads.map((c) => renderComment(c, false))
        )}
      </div>

      {toast && (
        <div
          className={`toast ${toast.type === "error" ? "error" : ""}`}
          data-testid="comment-toast"
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}
